'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import {
  loadFacetSetActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { CategoryMetadata } from '@lib/types/category';
import { buildCategoryHierarchy } from '@lib/util/buildCategoryHierarchy';
import {
  getCategoryDisplayName,
  getCategoryIconName,
} from '@lib/util/category';
import { DynamicIcon } from 'phosphor-icons/react';
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TreeAccordion, TreeNodeData } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { getCategoryController } from '../facetControllers';
import { useOptimisticFacetSelection } from '../hooks/useOptimisticFacetSelection';
import { useStableFacetValues } from '../hooks/useStableFacetValues';
import { CategoryFacetProps } from './CategoryFacet.types';

const CATEGORY_FACET_ID = 'ec_category';

/** Converts a Coveo category value ("seg1 > seg2") to a tree path ID ("seg1/seg2"). */
function coveoValueToTreeId(value: string): string {
  return value
    .split('>')
    .map((s) => s.trim())
    .join('/');
}

/** Collects all descendant IDs (path-based) from an enhanced tree node. */
function collectDescendantIds<T>(node: TreeNodeData<T>): string[] {
  const ids: string[] = [];
  if (node.children) {
    for (const child of node.children) {
      ids.push(child.id);
      ids.push(...collectDescendantIds(child));
    }
  }
  return ids;
}

/**
 * Finds the closest ancestor of `targetId` (path-based) that is explicitly
 * selected in Coveo state, i.e., the implicit parent covering the target.
 */
function findImplicitAncestor(
  targetId: string,
  coveoSelectedSet: Set<string>,
  tree: TreeNodeData<CategoryMetadata>[]
): TreeNodeData<CategoryMetadata> | null {
  const parts = targetId.split('/');
  for (let len = parts.length - 1; len > 0; len--) {
    const ancestorId = parts.slice(0, len).join('/');
    const ancestor = findNodeById(tree, ancestorId);
    if (
      ancestor?.metadata &&
      coveoSelectedSet.has(ancestor.metadata.coveoValue)
    ) {
      return ancestor;
    }
  }
  return null;
}

/**
 * When switching from implicit to explicit mode (a user deselects one node
 * from a parent that was implicitly covering its whole subtree), this returns
 * the Coveo values that should be added explicitly — all siblings of the
 * deselected path at every level, so the search still returns everything
 * except the deselected subtree.
 */
function expandImplicitForDeselection(
  targetId: string,
  node: TreeNodeData<CategoryMetadata>
): string[] {
  const toAdd: string[] = [];
  for (const child of node.children ?? []) {
    if (child.id === targetId) continue;
    if (targetId.startsWith(child.id + '/')) {
      toAdd.push(...expandImplicitForDeselection(targetId, child));
    } else {
      toAdd.push(child.metadata!.coveoValue);
    }
  }
  return toAdd;
}

function enhanceTreeWithIconsAndLabels(
  nodes: TreeNodeData<CategoryMetadata>[],
  searchValuesMicrocopy?: MicrocopyFragment[],
  parentPath: string[] = []
): TreeNodeData<CategoryMetadata>[] {
  return nodes.map((node) => {
    const currentPath = [...parentPath, node.id];
    const iconName = getCategoryIconName(node.id);

    return {
      ...node,
      id: currentPath.join('/'),
      label: getCategoryDisplayName(node.id, searchValuesMicrocopy as any),
      icon: iconName
        ? createElement(DynamicIcon, {
            iconName,
            size: 20,
            className: 'text-blue',
          })
        : undefined,
      children: node.children
        ? enhanceTreeWithIconsAndLabels(
            node.children,
            searchValuesMicrocopy,
            currentPath
          )
        : undefined,
    };
  });
}

function findNodeById(
  items: TreeNodeData<CategoryMetadata>[],
  targetId: string
): TreeNodeData<CategoryMetadata> | null {
  for (const item of items) {
    if (item.id === targetId) return item;
    if (item.children) {
      const found = findNodeById(item.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

export const CategoryFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: CategoryFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyFragment[];
  }>();

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const hasInitializedExpansion = useRef(false);

  const categoryTitle =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.categories')?.value ??
    'Categories';

  const eng = useEngine();
  const controller = useMemo(() => getCategoryController(eng), [eng]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(
    state.values,
    CATEGORY_FACET_ID,
    eng
  );
  const { optimisticValues, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const treeData = useMemo(() => {
    if (stableValues.length === 0) return [];
    return buildCategoryHierarchy(stableValues as any, '>');
  }, [stableValues]);

  const enhancedTreeData = useMemo(
    () => enhanceTreeWithIconsAndLabels(treeData, searchValuesMicrocopy),
    [treeData, searchValuesMicrocopy]
  );

  // Actual Coveo state — used by click handlers to determine toggle direction.
  const selectedValues = useMemo(
    () => stableValues.filter((v) => v.state === 'selected'),
    [stableValues]
  );

  // Optimistic state — used for all visual rendering (checkboxes, count badge).
  const optimisticSelectedValues = useMemo(
    () => optimisticValues.filter((v) => v.state === 'selected'),
    [optimisticValues]
  );

  /** Optimistic selected IDs (path-based). Used for count badge and expansion seeding. */
  const selectedIds = useMemo(
    () => optimisticSelectedValues.map((v) => coveoValueToTreeId(v.value)),
    [optimisticSelectedValues]
  );

  /**
   * Visual selected IDs for TreeAccordion. Extends optimistic selections with
   * implicitly-selected descendants: when a parent is in optimistic state but none of
   * its descendants are, all its descendants appear checked (implicit mode).
   */
  const visualSelectedIds = useMemo(() => {
    const optimisticSelectedSet = new Set(
      optimisticSelectedValues.map((v) => v.value)
    );
    const ids = new Set<string>(selectedIds);

    for (const v of optimisticSelectedValues) {
      const node = findNodeById(enhancedTreeData, coveoValueToTreeId(v.value));
      if (!node?.children?.length) continue;

      const descendantIds = collectDescendantIds(node);
      const hasExplicitDescendant = descendantIds.some((descId) => {
        const descNode = findNodeById(enhancedTreeData, descId);
        return (
          descNode?.metadata &&
          optimisticSelectedSet.has(descNode.metadata.coveoValue)
        );
      });

      if (!hasExplicitDescendant) {
        for (const descId of descendantIds) {
          ids.add(descId);
        }
      }
    }

    return Array.from(ids);
  }, [optimisticSelectedValues, enhancedTreeData, selectedIds]);

  // Seed expanded state once from initial selections (e.g. URL hash restoration)
  useEffect(() => {
    if (hasInitializedExpansion.current || selectedIds.length === 0) return;
    hasInitializedExpansion.current = true;

    const ancestorIds = selectedIds.flatMap((id) => {
      const parts = id.split('/');
      return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
    });

    if (ancestorIds.length > 0) {
      setExpandedIds(Array.from(new Set(ancestorIds)));
    }
  }, [selectedIds]);

  const handleExpandedChange = useCallback((newExpandedIds: string[]) => {
    setExpandedIds((prev) => {
      const collapsedIds = prev.filter((id) => !newExpandedIds.includes(id));
      if (collapsedIds.length === 0) return newExpandedIds;

      const filtered = newExpandedIds.filter(
        (id) =>
          !collapsedIds.some((collapsed) => id.startsWith(collapsed + '/'))
      );
      return filtered.length === newExpandedIds.length
        ? newExpandedIds
        : filtered;
    });
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      const node = findNodeById(enhancedTreeData, id);
      if (!node?.metadata) return;

      const facetValue = stableValues.find(
        (v) => v.value === node.metadata!.coveoValue
      );
      if (!facetValue) return;

      const valueSegments = facetValue.value.split('>').map((s) => s.trim());
      if (valueSegments.length !== node.metadata.fullPath.length) return;

      applyOptimistic([facetValue], () => controller.toggleSelect(facetValue));
      controller.toggleSelect(facetValue);
    },
    [enhancedTreeData, stableValues, controller, applyOptimistic]
  );

  /**
   * Handles multi-select interactions from TreeAccordion.
   *
   * Selecting a parent switches to implicit mode: only the parent enters
   * Coveo state. Its descendants appear checked visually but not in the URL,
   * keeping the URL compact (`f-ec_category=3d` instead of all subcategories).
   *
   * Deselecting a child from an implicit parent switches to explicit mode:
   * the implicit ancestor is removed and all siblings are added explicitly,
   * preserving the selection for everything except the deselected subtree.
   *
   * All changes are batch-dispatched with a single search to avoid N requests.
   */
  const handleSelectMany = useCallback(
    (ids: string[], selected: boolean) => {
      const coveoSelectedSet = new Set(selectedValues.map((v) => v.value));
      const stableValuesMap = new Map(stableValues.map((v) => [v.value, v]));

      const clickedNode = findNodeById(enhancedTreeData, ids[0]);
      if (!clickedNode?.metadata) return;

      const clickedFacetValue = stableValuesMap.get(
        clickedNode.metadata.coveoValue
      );

      const toToggle: (typeof stableValues)[number][] = [];

      const pushSelectedDescendants = () => {
        for (const descId of ids.slice(1)) {
          const descNode = findNodeById(enhancedTreeData, descId);
          if (!descNode?.metadata) continue;
          const descFv = stableValuesMap.get(descNode.metadata.coveoValue);
          if (descFv?.state === 'selected') toToggle.push(descFv);
        }
      };

      if (selected) {
        // Implicit select: add only ids[0], clean up any explicitly selected descendants.
        pushSelectedDescendants();
        if (clickedFacetValue && clickedFacetValue.state !== 'selected') {
          toToggle.push(clickedFacetValue);
        }
      } else {
        if (clickedFacetValue?.state === 'selected') {
          // Explicitly selected: remove it and any explicit descendants.
          toToggle.push(clickedFacetValue);
          pushSelectedDescendants();
        } else {
          // Implicitly shown (parent is implicit): switch to explicit mode.
          const implicitAncestor = findImplicitAncestor(
            ids[0],
            coveoSelectedSet,
            enhancedTreeData
          );
          if (implicitAncestor) {
            const ancestorFv = stableValuesMap.get(
              implicitAncestor.metadata!.coveoValue
            );
            if (ancestorFv?.state === 'selected') toToggle.push(ancestorFv);

            for (const coveoValue of expandImplicitForDeselection(
              ids[0],
              implicitAncestor
            )) {
              const fv = stableValuesMap.get(coveoValue);
              if (fv && fv.state !== 'selected') toToggle.push(fv);
            }
          } else {
            // All children explicitly selected; parent has no implicit ancestor to remove.
            pushSelectedDescendants();
          }
        }
      }

      if (toToggle.length === 0) return;

      const { toggleSelectFacetValue, updateFreezeCurrentValues } =
        loadFacetSetActions(eng);

      const dispatchToggles = (values: typeof toToggle) => {
        for (const value of values) {
          eng.dispatch(
            toggleSelectFacetValue({
              facetId: CATEGORY_FACET_ID,
              selection: value,
            })
          );
        }
        eng.dispatch(
          updateFreezeCurrentValues({
            facetId: CATEGORY_FACET_ID,
            freezeCurrentValues: false,
          })
        );
      };

      // Rollback only re-dispatches the state toggles — no analytics since the
      // user did not initiate a new search; Coveo still needs executeSearch to
      // refresh results with the reverted state.
      applyOptimistic(toToggle, () => {
        dispatchToggles(toToggle);
        eng.dispatch(
          loadSearchActions(eng).executeSearch(
            loadSearchAnalyticsActions(eng).logClearBreadcrumbs()
          )
        );
      });

      dispatchToggles(toToggle);
      eng.dispatch(
        loadSearchActions(eng).executeSearch(
          loadSearchAnalyticsActions(eng).logSearchFromLink()
        )
      );
    },
    [enhancedTreeData, stableValues, selectedValues, eng, applyOptimistic]
  );

  if (stableValues.length === 0) {
    return null;
  }

  const isDark = variant === 'mobile';

  const content = (
    <div>
      <TreeAccordion<CategoryMetadata>
        items={enhancedTreeData}
        isDarkTheme={isDark}
        selectionMode="multiple"
        selectedIds={visualSelectedIds}
        expandedIds={expandedIds}
        onExpandedChange={handleExpandedChange}
        onSelect={handleSelect}
        onSelectMany={handleSelectMany}
        size="default"
      />
    </div>
  );

  return (
    <FacetAccordion
      title={categoryTitle}
      variant={variant === 'horizontal' ? 'sidebar' : variant}
      className={className}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedIds.length}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
